import { useContext, useEffect, useRef, useState } from "react";
import { Card, CircularProgress, Icon, Slide, Box, Fade } from "@mui/material";
import { gql, useMutation } from "@apollo/client";
import Footer from "components/Footer";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { UserContext } from "context/user";
import SinDatos from "components/Carteles/SinDatos";
import PageLayout from "components/LayoutContainers/EmpleadoLayout";
import { getUproColor } from "utils/colors";
import { useLocation } from "react-router-dom";
import { esTeclaDeCodigo, leerTokenQrPedido } from "utils/qrPedido";

const SCANEAR_PEDIDO = gql`
  mutation scannearPedido($token: String!, $estado: String!, $destino: String) {
    scannearPedido(token: $token, estado: $estado, destino: $destino)
  }
`;

function PantallaScaner() {
  const [scannearPedido, { loading }] = useMutation(SCANEAR_PEDIDO);
  const [scannedCode, setScannedCode] = useState("");
  const [status, setStatus] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const route = useLocation();
  const dataRute = new URLSearchParams(route.search).get("destino");
  

  // Función para manejar el escaneo del QR

  // Agregar estilos de animación al document
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse {
        0% { opacity: 0.6; transform: scale(1); }
        50% { opacity: 1; transform: scale(1.05); }
        100% { opacity: 0.6; transform: scale(1); }
      }
      
      @keyframes bounce {
        0%, 20%, 53%, 80%, 100% { transform: translateY(0); }
        40%, 43% { transform: translateY(-15px); }
        70% { transform: translateY(-7px); }
        90% { transform: translateY(-3px); }
      }
      
      @keyframes shake {
        0%, 100% { transform: translateX(0); }
        10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
        20%, 40%, 60%, 80% { transform: translateX(5px); }
      }
      
      @keyframes fadeInUp {
        from { opacity: 0; transform: translateY(30px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `;
    document.head.appendChild(style);
    
    return () => document.head.removeChild(style);
  }, []);

  const codeRef = useRef(""); // ✅ persiste entre renders
  // Captura del escaneo
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "Enter") {
        if (codeRef.current) {
          setScannedCode(leerTokenQrPedido(codeRef.current));
          codeRef.current = ""; // reiniciar
        }
      } else if (esTeclaDeCodigo(e.key)) {
        codeRef.current += e.key;
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, []);

  // Enviar mutación cuando se obtiene un código
  useEffect(() => {
    if (!scannedCode) return;

    scannearPedido({
      variables: {
        token: scannedCode,
        estado: "EN PREPARACIÓN",
        destino: dataRute ? dataRute : undefined,
      },
    })
      .then((data) => {
        setStatus("success");
        setSuccessMsg(data?.data?.scannearPedido);
      })
      .catch((error) => {
        console.error(error?.message);
        setStatus("error");
        setErrorMsg(error?.message);
      })
      .finally(() => {
        setScannedCode(""); // limpiar
      });
  }, [scannedCode]);

  // Limpiar mensajes después de 12s
  useEffect(() => {
    if (!status) return;
    const timeoutId = setTimeout(() => setStatus(null), 6000);
    return () => clearTimeout(timeoutId);
  }, [status]);

  return (
    <PageLayout 
      background={`linear-gradient(135deg, rgba(44, 48, 135, 0.7) 0%, rgba(67, 56, 202, 0.6) 40%, rgba(156, 39, 176, 0.6) 70%, rgba(240, 5, 122, 0.7) 100%)`}
      sx={{
        minHeight: "100vh",
        backgroundAttachment: "fixed"
      }}
    >
      <SoftBox 
        display="grid" 
        gridTemplateColumns="1fr" 
        gap={1} 
        alignItems="start"
        sx={{
          minHeight: "100vh",
          padding: { xs: 1, sm: 2 }
        }}
      >
        <SoftBox display="flex" flexDirection="column" justifyContent="flex-start">
          <SoftBox p={{ xs: 1, sm: 2 }} mt={2}>
            <Fade in timeout={800}>
              <Card 
                sx={{ 
                  padding: { xs: 2, sm: 3 }, 
                  marginBottom: 2,
                  background: "linear-gradient(45deg, #ffffff 30%, #f8f9fa 90%)",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                  borderRadius: "16px",
                  border: "1px solid rgba(255,255,255,0.2)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.15)"
                  }
                }}
              >
                <SoftBox display="flex" alignItems="center" justifyContent="center" mb={1}>
                  <Icon 
                    sx={{ 
                      fontSize: "2rem", 
                      color: getUproColor("uproBlue"),
                      mr: 1,
                      filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))"
                    }}
                  >
                    local_shipping
                  </Icon>
                  <SoftTypography 
                    variant="h4" 
                    align="center"
                    sx={{
                      background: `linear-gradient(45deg, ${getUproColor("primary")}, ${getUproColor("uproPink")})`,
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      fontWeight: "bold"
                    }}
                  >
                    Entrega de Pedidos
                  </SoftTypography>
                </SoftBox>
                <SoftTypography 
                  variant="body2" 
                  align="center" 
                  color="text"
                  sx={{ opacity: 0.7 }}
                >
                  Sistema de gestión para la entrega de pedidos
                </SoftTypography>
              </Card>
            </Fade>

            {status === "success" ? (
              <Card
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  maxHeight: "90%",
                  width: { xs: "90%", sm: "70%", md: "45%" },
                  overflowY: "auto",
                  borderRadius: "20px",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
                  border: "none",
                  background: "rgba(255,255,255,0.95)",
                  backdropFilter: "blur(10px)",
                  zIndex: 1000
                }}
              >
                <Slide in={status === "success"} direction="up" mountOnEnter unmountOnExit>
                  <Card sx={{ borderRadius: "20px", overflow: "hidden" }}>
                    <SoftBox
                      sx={{
                        background: `linear-gradient(135deg, ${getUproColor("uproGreen")} 0%, #4CAF50 100%)`,
                        position: "relative",
                        overflow: "hidden",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: "-50%",
                          left: "-50%",
                          width: "200%",
                          height: "200%",
                          background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
                          animation: "pulse 2s ease-in-out infinite"
                        }
                      }}
                      p={4}
                      py={"6rem"}
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      alignItems="center"
                      textAlign="center"
                    >
                      <Box
                        sx={{
                          background: "rgba(255,255,255,0.2)",
                          borderRadius: "50%",
                          padding: "1rem",
                          mb: 2,
                          animation: "bounce 1s ease-out"
                        }}
                      >
                        <Icon
                          fontSize="large"
                          sx={{
                            color: "#fff",
                            fontSize: "3rem",
                            filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.3))"
                          }}
                        >
                          check_circle
                        </Icon>
                      </Box>
                      <SoftTypography 
                        variant={{ xs: "h4", sm: "h3" }}
                        color="white" 
                        fontWeight="bold" 
                        mt={2}
                        sx={{
                          textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                          filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
                          fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" }
                        }}
                      >
                        {successMsg || "Pedido entregado"}
                      </SoftTypography>
                      <SoftTypography 
                        variant="body1" 
                        color="white" 
                        mt={1}
                        sx={{
                          opacity: 0.9,
                          textShadow: "0 1px 2px rgba(0,0,0,0.2)"
                        }}
                      >
                        El pedido ha sido entregado correctamente.
                      </SoftTypography>
                    </SoftBox>
                  </Card>
                </Slide>
              </Card>
            ) : status === "error" ? (
              <Card
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  maxHeight: "90%",
                  width: { xs: "90%", sm: "70%", md: "45%" },
                  overflowY: "auto",
                  borderRadius: "20px",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
                  border: "none",
                  background: "rgba(255,255,255,0.95)",
                  backdropFilter: "blur(10px)",
                  zIndex: 1000
                }}
              >
                <Slide in={status === "error"} direction="up" mountOnEnter unmountOnExit>
                  <Card sx={{ borderRadius: "20px", overflow: "hidden" }}>
                    <SoftBox
                      sx={{
                        background: `linear-gradient(135deg, ${getUproColor("error")} 0%, #f44336 100%)`,
                        position: "relative",
                        overflow: "hidden",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: "-50%",
                          left: "-50%",
                          width: "200%",
                          height: "200%",
                          background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
                          animation: "pulse 2s ease-in-out infinite"
                        }
                      }}
                      p={4}
                      py={"6rem"}
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      alignItems="center"
                      textAlign="center"
                    >
                      <Box
                        sx={{
                          background: "rgba(255,255,255,0.2)",
                          borderRadius: "50%",
                          padding: "1rem",
                          mb: 2,
                          animation: "shake 0.8s ease-in-out"
                        }}
                      >
                        <Icon
                          fontSize="large"
                          sx={{
                            color: "#fff",
                            fontSize: "3rem",
                            filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.3))"
                          }}
                        >
                          warning
                        </Icon>
                      </Box>
                      <SoftTypography 
                        variant={{ xs: "h4", sm: "h3" }}
                        color="white" 
                        fontWeight="bold" 
                        mt={2}
                        sx={{
                          textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                          filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
                          fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" }
                        }}
                      >
                        {errorMsg || "El pedido ya fue entregado"}
                      </SoftTypography>
                    </SoftBox>
                  </Card>
                </Slide>
              </Card>
            ) : (
              <Card
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  maxHeight: "90%",
                  width: { xs: "90%", sm: "70%", md: "45%" },
                  overflowY: "auto",
                  borderRadius: "20px",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
                  border: "none",
                  background: "rgba(255,255,255,0.95)",
                  backdropFilter: "blur(10px)",
                  zIndex: 1000
                }}
              >
                <Slide
                  in={status != "success" && status != "error"}
                  direction="up"
                  mountOnEnter
                  unmountOnExit
                >
                  <Card sx={{ borderRadius: "20px", overflow: "hidden" }}>
                    <SoftBox
                      sx={{
                        background: loading 
                          ? `linear-gradient(135deg, ${getUproColor("uproBlue")} 0%, ${getUproColor("uproGreen")} 100%)`
                          : getUproColor("uproGray"),
                        position: "relative",
                        overflow: "hidden",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: "-50%",
                          left: "-50%",
                          width: "200%",
                          height: "200%",
                          background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
                          animation: "pulse 2s ease-in-out infinite"
                        }
                      }}
                      p={4}
                      py={"6rem"}
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      alignItems="center"
                      textAlign="center"
                    >
                      <Box
                        sx={{
                          background: "rgba(255,255,255,0.2)",
                          borderRadius: "50%",
                          padding: "1.5rem",
                          mb: 3,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                      >
                        {loading ? (
                          <CircularProgress 
                            size={60} 
                            sx={{ 
                              color: "#fff",
                              filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.3))"
                            }} 
                          />
                        ) : (
                          <Icon
                            fontSize="large"
                            sx={{
                              color: "#fff",
                              fontSize: "3rem",
                              animation: "pulse 2s ease-in-out infinite",
                              filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.3))"
                            }}
                          >
                            qr_code_scanner
                          </Icon>
                        )}
                      </Box>
                      <SoftTypography 
                        variant={{ xs: "h5", sm: "h4" }}
                        color="white" 
                        fontWeight="bold" 
                        mt={1}
                        sx={{
                          textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                          filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
                          fontSize: { xs: "1.25rem", sm: "1.5rem", md: "2rem" }
                        }}
                      >
                        {loading ? "Procesando pedido..." : "Escanea el código QR"}
                      </SoftTypography>
                      <SoftTypography 
                        variant="body1" 
                        color="white" 
                        mt={2}
                        sx={{
                          opacity: 0.9,
                          textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                          maxWidth: "80%"
                        }}
                      >
                        {loading
                          ? "Espere mientras se procesa y factura el pedido en ARCA"
                          : "Para finalizar un pedido, escanee el código QR del pedido."}
                      </SoftTypography>
                      {!loading && (
                        <SoftBox mt={3}>
                          <SoftTypography 
                            variant="caption" 
                            color="white" 
                            sx={{
                              opacity: 0.7,
                              fontStyle: "italic"
                            }}
                          >
                            💡 Posiciona el código QR frente al escáner
                          </SoftTypography>
                        </SoftBox>
                      )}
                    </SoftBox>
                  </Card>
                </Slide>
              </Card>
            )}
          </SoftBox>
        </SoftBox>
      </SoftBox>
    </PageLayout>
  );
}

export default PantallaScaner;
